//import EQError from "../../../../../utils/EQError";
import * as RawKpiConstants from "../enums/RawKpiConstants";
import * as Bearer from "./enums/BearerEnum";
import * as IpProtocol from "./enums/IpProtocolEnum";

export default class KpiConverter {
    /**
     * Constructor
     *
     * @param brand
     * @param ip
     */
    constructor(brand, ip) {
        this.brand = brand;
        this.ip = ip;
    }

    /**
     * Return a raw kpi object
     *
     * @returns {Object}
     */
    getRawKpi() {
        const rawKpi = {};
        Object.keys(RawKpiConstants).forEach((kpiKey) => {
            rawKpi[kpiKey] = null;
        });
        rawKpi[RawKpiConstants.BRAND] = this.brand;
        rawKpi[RawKpiConstants.LOCAL_IP] = this.ip;
        rawKpi[RawKpiConstants.LOCAL_IP_PROTOCOL] = IpProtocol.VERSION_4;
        rawKpi[RawKpiConstants.TIMESTAMP] = Date.now();

        return rawKpi;
    }

    /**
     * Access to the raw kpi constants without import
     */
    getRawKpiConstants() {
        return RawKpiConstants;
    }

    /**
     * Set api cell id
     *
     * @param rawData Input
     * @param rawKpi Output
     */
    cellId(rawData, rawKpi) {
        if (rawKpi[RawKpiConstants.CELL_ID] && rawKpi[RawKpiConstants.BEARER]) {
            switch(rawKpi[RawKpiConstants.BEARER].code) {
                case Bearer.GPRS.code:
                case Bearer.EDGE.code:
                case Bearer.GSM.code:
                case Bearer.GSM_RAT.code:
                case Bearer.TYPE_2G.code:
                    rawKpi[RawKpiConstants.CELL_ID] = rawKpi[RawKpiConstants.CELL_ID] & 0xFFFF;
                    break;
                case Bearer.UMTS.code:
                case Bearer.CDMA.code:
                case Bearer.EVDO_0.code:
                case Bearer.EVDO_A.code:
                case Bearer.EVDO_B.code:
                case Bearer.C1XRTT.code:
                case Bearer.HSDPA.code:
                case Bearer.HSUPA.code:
                case Bearer.HSPA.code:
                case Bearer.IDEN.code:
                case Bearer.EHRPD.code:
                case Bearer.HSPAP.code:
                case Bearer.DUAL_CARRIER.code:
                case Bearer.TYPE_3G.code:
                    rawKpi[RawKpiConstants.CELL_ID] = rawKpi[RawKpiConstants.CELL_ID] & 0xFFFF;
                    break;
                case Bearer.LTE.code:
                case Bearer.TYPE_4G.code:
                case Bearer.LTE_A.code:
                    // padStart function definition if unexisting (string prototype)
                    // https://github.com/uxitten/polyfill/blob/master/string.polyfill.js
                    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/padStart
                    if (!String.prototype.padStart) {
                        String.prototype.padStart = function padStart(targetLength,padString) {
                            targetLength = targetLength>>0; //truncate if number or convert non-number to 0;
                            padString = String((typeof padString !== 'undefined' ? padString : ' '));
                            if (this.length > targetLength) {
                                return String(this);
                            }
                            else {
                                targetLength = targetLength-this.length;
                                if (targetLength > padString.length) {
                                    padString += padString.repeat(targetLength/padString.length); //append to original to ensure we are longer than needed
                                }
                                return padString.slice(0,targetLength) + String(this);
                            }
                        };
                    }
                    rawKpi[RawKpiConstants.CELL_ID_4G] = parseInt(parseInt(rawKpi[RawKpiConstants.CELL_ID], 10).toString(2).padStart(28, "0").substr(20), 2)
                    break;
            }
        }
    }

    /**
     * Set api rad band
     *
     * @param rawData Input
     * @param rawKpi Output
     */
    radBand(rawData, rawKpi) {
        const bands = [];
        const createBandObject = (band, label, lowRange, highRange) => {
            return {band: band, label: label, lowRange: lowRange, highRange: highRange};
        };

        bands.push(createBandObject(1, "2100", 0, 600));
        bands.push(createBandObject(2, "1900", 600, 1200));
        bands.push(createBandObject(3, "1800", 1200, 1950));
        bands.push(createBandObject(4, "1700", 1950, 2400));
        bands.push(createBandObject(5, "850", 2400, 2650));
        bands.push(createBandObject(6, "UMTS only", 2650, 2750));
        bands.push(createBandObject(7, "2600", 2750, 3450));
        bands.push(createBandObject(8, "900 GSM", 3450, 3800));
        bands.push(createBandObject(9, "1800", 3800, 4150));
        bands.push(createBandObject(10, "AWS-1+", 4150, 4750));
        bands.push(createBandObject(11, "1500 Lower", 4750, 4950));
        bands.push(createBandObject(12, "700 a", 5010, 5180));
        bands.push(createBandObject(13, "700 c", 5180, 5280));
        bands.push(createBandObject(14, "700 PS", 5280, 5380));
        bands.push(createBandObject(17, "700 b", 5730, 5850));
        bands.push(createBandObject(18, "800 Lower", 5850, 6000));
        bands.push(createBandObject(19, "800 Upper", 6000, 6150));
        bands.push(createBandObject(20, "800 DD", 6150, 6450));
        bands.push(createBandObject(21, "1500 Upper", 6450, 6600));
        bands.push(createBandObject(22, "3500", 6600, 7400));
        bands.push(createBandObject(23, "2000 S-band", 7500, 7700));
        bands.push(createBandObject(24, "1600 L-band", 7700, 8040));
        bands.push(createBandObject(25, "1900+", 8040, 8690));
        bands.push(createBandObject(26, "850+", 8690, 9040));
        bands.push(createBandObject(27, "800 SMR", 9040, 9210));
        bands.push(createBandObject(28, "700 APT", 9210, 9660));
        bands.push(createBandObject(29, "700 d", 9660, 9770));
        bands.push(createBandObject(30, "2300 WCS", 9770, 9870));
        bands.push(createBandObject(31, "450", 9870, 9920));
        bands.push(createBandObject(32, "1500 L-band", 9920, 10360));
        bands.push(createBandObject(33, "TD 1900", 36000, 36200));
        bands.push(createBandObject(34, "TD 2000", 36200, 36350));
        bands.push(createBandObject(35, "TD PCS Lower", 36350, 36950));
        bands.push(createBandObject(36, "TD PCS Upper", 36950, 37550));
        bands.push(createBandObject(37, "TD PCS Center gap", 37550, 37750));
        bands.push(createBandObject(38, "TD 2600", 37750, 38250));
        bands.push(createBandObject(39, "TD 1900+", 38250, 38650));
        bands.push(createBandObject(40, "TD 2300", 38650, 39650));
        bands.push(createBandObject(41, "TD 2500", 39650, 41590));
        bands.push(createBandObject(42, "TD 3500", 41590, 43590));
        bands.push(createBandObject(43, "TD 3700", 43590, 45590));
        bands.push(createBandObject(44, "TD 700", 45590, 46590));
        bands.push(createBandObject(45, "TD 1500", 46590, 46790));
        bands.push(createBandObject(46, "TD Unlicensed", 46790, 54540));
        bands.push(createBandObject(47, "TD V2X", 54540, 55240));
        bands.push(createBandObject(48, "TD 3600", 55240, 56740));
        bands.push(createBandObject(49, "TD 3600r", 56740, 58240));
        bands.push(createBandObject(50, "TD 1500+", 58240, 59090));
        bands.push(createBandObject(51, "TD 1500-", 59090, 59140));
        bands.push(createBandObject(52, "TD 3300", 59140, 60140));
        bands.push(createBandObject(65, "2100", 65536, 66436));
        bands.push(createBandObject(66, "1700", 66436, 67336));
        bands.push(createBandObject(67, "700", 67336, 67536));
        bands.push(createBandObject(68, "700", 67536, 67836));
        bands.push(createBandObject(69, "2600", 67836, 68336));
        bands.push(createBandObject(70, "2000", 68336, 68586));
        bands.push(createBandObject(71, "600", 68586, 68936));
        bands.push(createBandObject(72, "450", 68936, 68986));
        bands.push(createBandObject(73, "450", 68986, 69036));
        bands.push(createBandObject(74, "1500", 69036, 69466));
        bands.push(createBandObject(75, "1500", 69466, 70316));
        bands.push(createBandObject(76, "1500", 70316, 70366));
        bands.push(createBandObject(85, "700", 70366, 70546));
        bands.push(createBandObject(252, "Unlicensed NII-1", 255144, 256144));
        bands.push(createBandObject(255, "Unlicensed NII-3", 260894, 262144));

        let fBand = null;
        if (rawKpi[RawKpiConstants.EARFCN]) {
            bands.forEach((band) => {
                if (rawKpi[RawKpiConstants.EARFCN] >= band.lowRange && rawKpi[RawKpiConstants.EARFCN] < band.highRange) {
                    fBand = band;
                }
            });
        }

        if (fBand) {
            rawKpi[RawKpiConstants.RAD_BAND] = fBand.band;
        }

    }

    /**
     * Convert a raw data to a raw kpi
     *
     * @param rawData
     * @param converters
     * @returns {Promise}
     */
    convert(rawData, converters) {
        const rawKpi = this.getRawKpi();
        return new Promise((resolve) => {
            converters.forEach((cMethod) => {
                cMethod(rawData, rawKpi);
            });

            resolve(rawKpi);
        });
    }
}


// WEBPACK FOOTER //
// ./js/core/gateway/service/modules/kpi/converter/KpiConverter.js